import React from 'react';
import {Button} from '@lightricks/react-design-system';
import CREATOR_PROFILE_TEST_IDS from '@/components/creator-profile/testIds';
import styles from './HeaderActions.module.scss';
import HeaderActionsOverflow from './HeaderActionsOverflow';
import type {CtaDescriptor} from './HeaderActionsTypes';

/**
 * Three visual tiers: primary (filled), secondary (tinted), overflow (kebab).
 * Decides only how to render — Header.tsx decides which CTA goes where via
 * the source-aware config in profileSources.ts.
 */
export interface HeaderActionsProps {
  testID?: string;
  primary?: CtaDescriptor;
  secondary?: CtaDescriptor[];
  overflow?: CtaDescriptor[];
}

function HeaderActions({
  testID = CREATOR_PROFILE_TEST_IDS.HEADER__ACTIONS,
  primary,
  secondary = [],
  overflow = [],
}: HeaderActionsProps) {
  const visibleSecondary = secondary.filter((cta) => cta.visible !== false);
  const visibleOverflow = overflow.filter((cta) => cta.visible !== false);
  const primaryVisible = primary && primary.visible !== false ? primary : null;

  const hasAnyVisible =
    !!primaryVisible ||
    visibleSecondary.length > 0 ||
    visibleOverflow.length > 0;

  if (!hasAnyVisible) {
    return null;
  }

  return (
    <div className={styles.actionsRow} data-testid={testID}>
      {primaryVisible ? (
        <Button
          testID={primaryVisible.testID ?? `header__cta-${primaryVisible.id}`}
          className={styles.actionButton}
          appearance="neutral"
          mode="filled"
          size="small"
          onClick={primaryVisible.onClick}
          disabled={primaryVisible.disabled || primaryVisible.isLoading}
          isLoading={primaryVisible.isLoading}
          title={primaryVisible.tooltip}
        >
          {primaryVisible.label}
        </Button>
      ) : null}
      {visibleSecondary.map((cta) => (
        <Button
          key={cta.id}
          testID={cta.testID ?? `header__cta-${cta.id}`}
          className={styles.actionButton}
          appearance="neutral"
          mode="tinted"
          size="small"
          onClick={cta.onClick}
          disabled={cta.disabled || cta.isLoading}
          isLoading={cta.isLoading}
          title={cta.tooltip}
        >
          {cta.label}
        </Button>
      ))}
      <HeaderActionsOverflow
        testID={CREATOR_PROFILE_TEST_IDS.HEADER__OVERFLOW_MENU}
        ctas={overflow}
      />
    </div>
  );
}

export default HeaderActions;
